import { FilterColors, FilterFonts } from "../../state/filter";
import {
	PageElementsFamily,
	PageSizeAtom,
	createLocation,
} from "../../state/page";
import {
	ReactElement,
	memo,
	useContext,
	useLayoutEffect,
	useRef,
	useState,
} from "react";
import { font, fontSize, opacityHelper, resolveColorChoice } from "../../lib/cssHelpers";
import styled, { DefaultTheme, ThemeProvider } from "styled-components";

import { ElementProps } from "../../functionalComponents/RenderPage";
import { HostId } from "../../state/ui";
import { IsInFlexBoxContext } from "../displayElements/Cell";
import { LineProps } from "../../@types/viewerati";
import MarkDownToTag from "../../lib/markdown/MarkDown";
import { OwnerName } from "../../state/selectors";
import modifierStyled from "../../lib/modifierStyled";
import { strings } from "@quickpoint.me/shared";
import { useRecoilValue } from "recoil";
import useTappedAnimation from "../../lib/animations/tappedAnimationHook";

const lineFilter = (theme: DefaultTheme) => {
	return theme.line;
};

const StyledLine = styled.div<LineProps>`
  ${(props) => opacityHelper(props.$tapped, props.theme.opacity)};
  visibility: ${(props) => (props.$tapped ? "visible" : "hidden")};
  font-family: ${(props) => font(props.theme, props.$fonts)};
  font-size: ${(props) => fontSize(props.theme, props.$baseFontSize)};
  opacity: ${(props) => opacityHelper(props.$tapped, props.theme.opacity)};
  position: relative;
  width: 100%;
  
  .hr-plain {
    border: 0;
    height: ${(props) => `${props.$hrHeight * (props.$baseFontSize / 5)}px`};
    background-color: ${(props) => resolveColorChoice(props.theme.color, props.$filterColors)};
  }
  .hr-text {
    border: 0;
    color: ${(props) => resolveColorChoice(props.theme.color, props.$filterColors)};
    height: ${(props) => fontSize(props.theme, props.$baseFontSize)};
    line-height: ${(props) => `${props.$baseFontSize}px`};
    opacity: 0.9;
    outline: 0;
    position: relative;
    text-align: center;

   
    &:before {
      content: "";
      /* use the linear-gradient for the fading effect */
      /* use a solid background color for a solid bar */
      position: absolute;
      left: 0;
      top: 50%;
      width: 100%;
      height: ${(props) => `${props.$baseFontSize / 7}px`};
      background: ${(props) =>
				`linear-gradient(to right, transparent, ${resolveColorChoice(props.theme.color, props.$filterColors)} ${
					props.$contentLocation.left - props.$baseFontSize
				}px, transparent ${
					props.$contentLocation.left - props.$baseFontSize
				}px, transparent ${
					props.$contentLocation.left +
					props.$contentLocation.width +
					props.$baseFontSize
				}px, ${resolveColorChoice(props.theme.color, props.$filterColors)}   ${
					props.$contentLocation.left +
					props.$contentLocation.width +
					props.$baseFontSize
				}px , transparent)`}}
    
  }
  ${(props) => `${modifierStyled("line:", props)}`}
`;

const StyledContent = styled.div<{
	$contentLocation: LineProps["$contentLocation"];
}>`
  position: absolute;
  color: ${(props) => props.theme.color};
  top: ${(props) => `${props.$contentLocation.top}px`};
  left: ${(props) => `${props.$contentLocation.left}px`};
`;

const Line = ({
	atomId,
	pageIndex,
}: ElementProps): ReactElement<ElementProps> => {
	const hostId = useRecoilValue(HostId);
	const key = createLocation(pageIndex, atomId);
	const elementData = useRecoilValue(PageElementsFamily(key)).data;
	const isInFlexBox = useContext(IsInFlexBoxContext);
	const filterColors = useRecoilValue(FilterColors);
	const pageSize = useRecoilValue(PageSizeAtom);
	const [contentLocation, setContentLocation] = useState<
		LineProps["$contentLocation"]
	>({ top: 0, left: 0, width: 0 });

	const fonts = useRecoilValue(FilterFonts);
	const baseFontSize = pageSize.baseFontSize;
	const tapped = elementData.tapped;
	const ownerName = useRecoilValue(OwnerName);

	const elRef = useRef<HTMLDivElement>(null);
	const contentRef = useRef<HTMLDivElement>(null);

	useTappedAnimation({ elRef, tapped, atomId });

	let hasText = false;
	let hrHeight = 1;

	if (typeof elementData.content === "string") {
		hrHeight = parseInt(elementData.content);
		if (Number.isNaN(hrHeight)) {
			hrHeight = 1;
			hasText = true;
		}
	}

	const content =
		typeof elementData.content === "string"
			? MarkDownToTag({
					text: elementData.content,
					ownerName,
					modifier: elementData.modifier,
					hostId,
					tapped,
			  })
			: elementData.content;

	const backgroundColor = elementData.modifier?.["background:"]
		? elementData.modifier?.["background:"]
		: "unset";

	// measure text to set size of linear gradient
	useLayoutEffect(() => {
		content;
		pageSize;
		if (elRef.current && hasText && contentRef.current) {
			const container = elRef.current;
			const content = contentRef.current;
			const containerWidth = container.offsetWidth;
			const contentWidth = content.offsetWidth;
			const containerHeight = container.offsetHeight;
			const contentHeight = content.offsetHeight;
			const contentLeft = containerWidth / 2 - contentWidth / 2;
			const contentTop = containerHeight / 2 - contentHeight / 2;
			setContentLocation({
				top: contentTop,
				left: contentLeft,
				width: contentWidth,
			});
		}
	}, [content, hasText, pageSize]);

	return (
		<ThemeProvider theme={lineFilter}>
			<StyledLine
				$baseFontSize={baseFontSize}
				$baseFontSizeRaw={pageSize.baseFontSizeRaw}
				$backgroundColor={backgroundColor}
				$elementIndex={atomId}
				$isInFlexBox={isInFlexBox}
                                $filterColors={filterColors}
                                $contentLocation={contentLocation}
                                $fonts={fonts}
                                $pageSize={pageSize}
                                $isInPortrait={!pageSize.landscape}
                                $onePercentHeight={pageSize.onePercentHeight}
                                $onePercentWidth={pageSize.onePercentWidth}
                                $hrHeight={hrHeight}
				$modifier={elementData.modifier}
				$tapped={tapped}
				/* a rule with no label is pure decoration — hide the whole element
				   or its empty wrapper becomes a silent stop for screen readers */
				aria-hidden={!elementData.tapped || !hasText}
				className={`${strings.css.pageElement}  line`}
				data-atom-id={elementData.id}
				ref={elRef}
			>
				{/* the rule is decoration — Safari maps <hr> to an interactive
				    "splitter" for VoiceOver, hints and all; only the label text
				    (if any) is content */}
				{!hasText && <hr className="hr-plain" aria-hidden="true" />}
				{hasText && elementData.content && (
					<>
						<hr className="hr-text" aria-hidden="true" />
						<StyledContent $contentLocation={contentLocation} ref={contentRef}>
							{content}
						</StyledContent>
					</>
				)}
			</StyledLine>
		</ThemeProvider>
	);
};

export default memo(Line);
